import { computed, h } from 'vue';

import { useState } from '@components/composable';
import { classesStates, classNames } from '@components/utils';

import { CdsIcon } from '../../CdsIcon';
import { CdsTooltip } from '../../CdsTooltip';
import { getColumnKey, getColumnPos, renderColumnTitle } from '../utils';

import type { NTable } from '../interface';
import type { ComponentProps } from '@components/utils';
import type { Ref, VNode, ComputedRef } from 'vue';

export const ASCEND = 'ascend';
export const DESCEND = 'descend';

export interface ISorterOptions<RecordType> {
  mergedColumns: ComputedRef<NTable.NColumn.TColumns<RecordType>>;
  sortDirections: ComputedRef<NTable.TSortOrder[]>;
  tableLocale?: Ref<NTable.ILocale>;
  showSorterTooltip?: Ref<boolean | ComponentProps<typeof CdsTooltip>>;
  onSorterChange: (
    sorterResult: NTable.ISorterResult<RecordType> | NTable.ISorterResult<RecordType>[],
    sortStates: NTable.ISortState<RecordType>[]
  ) => void;
}

export interface IUseSorter<RecordType> {
  transformColumns: NTable.NColumn.TTransformColumns<RecordType>;
  mergedSorterStates: Ref<NTable.ISortState<RecordType>[]>;
  columnTitleSorterProps: Ref<NTable.NColumn.ITitleProps<RecordType>>;
  sorters: Ref<NTable.ISorterResult<RecordType> | NTable.ISorterResult<RecordType>[]>;
}

export function useSorter<RecordType>({
  mergedColumns,
  sortDirections,
  tableLocale,
  showSorterTooltip,
  onSorterChange
}: ISorterOptions<RecordType>): IUseSorter<RecordType> {
  const [sortState, setSortState] = useState<NTable.ISortState<RecordType>[]>(
    collectSortStates(mergedColumns.value, true)
  );

  const mergedSorterStates = computed(() => {
    let validate = true;
    const collectedStates = collectSortStates(mergedColumns.value, false);

    if (!collectedStates.length) return sortState.value;

    const validateStates: NTable.ISortState<RecordType>[] = [];

    const patchStates = (state: NTable.ISortState<RecordType>) => {
      if (validate) {
        validateStates.push(state);
      } else {
        validateStates.push({
          ...state,
          sortOrder: null as unknown as any
        });
      }
    };

    let multipleMode: boolean | null = null;
    collectedStates.forEach((state) => {
      if (multipleMode === null) {
        patchStates(state);

        if (state.sortOrder) {
          if (state.multiplePriority === false) {
            validate = false;
          } else {
            multipleMode = true;
          }
        }
      } else if (multipleMode && state.multiplePriority !== false) {
        patchStates(state);
      } else {
        validate = false;
        patchStates(state);
      }
    });

    return validateStates;
  });

  const columnTitleSorterProps = computed<NTable.NColumn.ITitleProps<RecordType>>(() => {
    const sortColumns = mergedSorterStates.value.map(({ column, sortOrder }) => ({
      column,
      order: sortOrder
    }));

    return { sortColumns };
  });

  const triggerSort = (sortState: NTable.ISortState<RecordType>) => {
    let newSorterStates;

    if (
      sortState.multiplePriority === false ||
      !mergedSorterStates.value.length ||
      mergedSorterStates.value[0].multiplePriority === false
    ) {
      newSorterStates = [sortState];
    } else {
      newSorterStates = [...mergedSorterStates.value.filter(({ key }) => key !== sortState.key), sortState];
    }

    setSortState(newSorterStates);
    onSorterChange(generateSorterInfo(newSorterStates), newSorterStates);
  };

  const transformColumns = (innerColumns: NTable.NColumn.TColumns<RecordType>) =>
    injectSorter(
      innerColumns,
      mergedSorterStates.value,
      triggerSort,
      sortDirections.value,
      tableLocale?.value,
      showSorterTooltip?.value
    );

  const sorters = computed(() => generateSorterInfo(mergedSorterStates.value));

  return {
    transformColumns,
    mergedSorterStates,
    columnTitleSorterProps,
    sorters
  };
}

export function getSortData<RecordType>(
  data: readonly RecordType[],
  sortStates: NTable.ISortState<RecordType>[],
  childrenColumnName: string
): RecordType[] {
  const innerSorterStates = sortStates
    .slice()
    .sort((a, b) => (b.multiplePriority as number) - (a.multiplePriority as number));

  const cloneData = data.slice();

  const runningSorters = innerSorterStates.filter(
    ({ column: { sorter }, sortOrder }) => getSortFunction(sorter) && sortOrder
  );

  if (!runningSorters.length) return cloneData;

  return cloneData
    .sort((record1, record2) => {
      for (let i = 0; i < runningSorters.length; i += 1) {
        const sorterState = runningSorters[i];
        const {
          column: { sorter },
          sortOrder
        } = sorterState;
        const compareFn = getSortFunction(sorter);

        if (compareFn && sortOrder) {
          const compareResult = compareFn(record1, record2, sortOrder);

          if (compareResult !== 0) {
            return sortOrder === ASCEND ? compareResult : -compareResult;
          }
        }
      }

      return 0;
    })
    .map<RecordType>((record) => {
      const subRecords = (record as any)[childrenColumnName];

      if (subRecords) {
        return {
          ...record,
          [childrenColumnName]: getSortData(subRecords, innerSorterStates, childrenColumnName)
        };
      }

      return record;
    });
}

function injectSorter<RecordType>(
  columns: NTable.NColumn.TColumns<RecordType>,
  sorterStates: NTable.ISortState<RecordType>[],
  triggerSorter: (sorterState: NTable.ISortState<RecordType>) => void,
  defaultSortDirections: NTable.TSortOrder[],
  tableLocale?: NTable.ILocale,
  tableShowSorterTooltip?: boolean | ComponentProps<typeof CdsTooltip>,
  pos?: string
): NTable.NColumn.TColumns<RecordType> {
  return (columns || []).map((column, index) => {
    const columnPos = getColumnPos(index, pos);
    let newColumn: NTable.NColumn.TColumns<RecordType>[number] = column;

    if (newColumn.sorter) {
      const sortDirection: NTable.TSortOrder[] = newColumn.sortDirections || defaultSortDirections;
      const showSorterTooltip =
        newColumn.showSorterTooltip === undefined ? tableShowSorterTooltip : newColumn.showSorterTooltip;
      const columnKey = getColumnKey(newColumn, columnPos);
      const sorterState = sorterStates.find(({ key }) => key === columnKey);
      const sorterOrder = sorterState ? sorterState.sortOrder : null;
      const nextSortOrder = nextSortDirection(sortDirection, sorterOrder);
      const sortNode =
        sortDirection.length &&
        h(
          'div',
          {
            class: {
              'cds-table__column-sorter-icon': true,
              [classesStates.active]: sorterOrder === DESCEND || sorterOrder === ASCEND
            }
          },
          h(CdsIcon, {
            name: (sorterOrder === ASCEND && 'sorting-up') || (sorterOrder === DESCEND && 'sorting-down') || 'sorting'
          })
        );
      const { cancelSort, triggerAsc, triggerDesc } = tableLocale || {};

      let sortTip: string | undefined = cancelSort;
      if (nextSortOrder === DESCEND) {
        sortTip = triggerDesc;
      } else if (nextSortOrder === ASCEND) {
        sortTip = triggerAsc;
      }

      const tooltipProps: ComponentProps<typeof CdsTooltip> =
        typeof showSorterTooltip === 'object' ? showSorterTooltip : { text: sortTip };

      newColumn = {
        ...newColumn,
        class: `${newColumn.class || ''} ${sorterOrder ? 'cds-table__column-sort' : ''}`,
        title: (renderProps: NTable.NColumn.ITitleProps<RecordType>) => {
          const renderSortTitle = (tooltipProps: ComponentProps<typeof CdsTooltip>) =>
            h('div', { class: 'cds-table__column-sorters', ...tooltipProps }, [
              h('span', { class: 'cds-table__column-title' }, renderColumnTitle(column.title, renderProps) as VNode),
              h(
                'span',
                {
                  class: classNames('cds-table__column-sorter', {
                    'cds-table__column-sorter-full': !!sortNode
                  })
                },
                h('span', { class: 'cds-table__column-sorter-inner' }, [sortNode])
              )
            ]);

          return showSorterTooltip
            ? h(
                CdsTooltip,
                { ...tooltipProps, offset: 24, openDelay: 100 },
                { activator: ({ props }: { props: ComponentProps<typeof CdsTooltip> }) => renderSortTitle(props) }
              )
            : renderSortTitle({});
        },
        customHeaderCell: (col) => {
          const cell = (column.customHeaderCell && column.customHeaderCell(col)) || {};
          const originOnClick = cell.onClick;

          cell.onClick = (event: MouseEvent) => {
            triggerSorter({
              column,
              key: columnKey,
              sortOrder: nextSortOrder,
              multiplePriority: getMultiplePriority(column)
            });

            if (originOnClick) {
              originOnClick(event);
            }
          };

          cell.class = `${cell.class || ''} cds-table__column-has-sorters`;

          return cell;
        }
      };
    }

    if ('children' in newColumn) {
      newColumn = {
        ...newColumn,
        children: injectSorter(
          newColumn.children,
          sorterStates,
          triggerSorter,
          defaultSortDirections,
          tableLocale,
          tableShowSorterTooltip,
          columnPos
        )
      };
    }

    return newColumn;
  });
}

function getSortFunction<RecordType>(
  sorter: NTable.NColumn.IColumn<RecordType>['sorter']
): NTable.TCompareFn<RecordType> | false {
  if (typeof sorter === 'function') return sorter;
  if (sorter && typeof sorter === 'object' && sorter.compare) return sorter.compare;
  return false;
}

function getMultiplePriority<RecordType>(column: NTable.NColumn.IColumn<RecordType>): number | false {
  if (typeof column.sorter === 'object' && typeof column.sorter.multiple === 'number') {
    return column.sorter.multiple;
  }
  return false;
}

function collectSortStates<RecordType>(
  columns: NTable.NColumn.TColumns<RecordType>,
  init: boolean,
  pos?: string
): NTable.ISortState<RecordType>[] {
  let sortStates: NTable.ISortState<RecordType>[] = [];

  function pushState(column: NTable.NColumn.TColumns<RecordType>[number], columnPos: string) {
    sortStates.push({
      column,
      key: getColumnKey(column, columnPos),
      multiplePriority: getMultiplePriority(column),
      sortOrder: column.sortOrder!
    });
  }

  (columns || []).forEach((column, index) => {
    const columnPos = getColumnPos(index, pos);

    if ((column as NTable.NColumn.IColumnGroup<RecordType>).children) {
      if ('sortOrder' in column) {
        pushState(column, columnPos);
      }
      sortStates = [
        ...sortStates,
        ...collectSortStates((column as NTable.NColumn.IColumnGroup<RecordType>).children, init, columnPos)
      ];
    } else if (column.sorter) {
      if ('sortOrder' in column) {
        pushState(column, columnPos);
      } else if (init && column.defaultSortOrder) {
        sortStates.push({
          column,
          key: getColumnKey(column, columnPos),
          multiplePriority: getMultiplePriority(column),
          sortOrder: column.defaultSortOrder!
        });
      }
    }
  });

  return sortStates;
}

function stateToInfo<RecordType>(sorterState: NTable.ISortState<RecordType>): NTable.ISorterResult<RecordType> {
  const { column, sortOrder } = sorterState;

  return {
    column,
    order: sortOrder,
    field: column.dataIndex,
    columnKey: column.key
  };
}

function generateSorterInfo<RecordType>(
  sorterStates: NTable.ISortState<RecordType>[]
): NTable.ISorterResult<RecordType> | NTable.ISorterResult<RecordType>[] {
  const list = sorterStates.filter(({ sortOrder }) => sortOrder).map(stateToInfo);

  if (list.length === 0 && sorterStates.length) {
    return {
      ...stateToInfo(sorterStates[sorterStates.length - 1]),
      column: undefined
    };
  }

  if (list.length <= 1) {
    return list[0] || {};
  }

  return list;
}

function nextSortDirection(sortDirections: NTable.TSortOrder[], current: NTable.TSortOrder | null) {
  if (!current) return sortDirections[0];
  return sortDirections[sortDirections.indexOf(current) + 1];
}
